import { useMemo } from 'react';
import {
  calculateCollectionRate,
  calculateAveragePaymentTime,
  calculatePaymentSuccessRate,
  calculateRevenueGrowth,
  generateSparklineData,
} from '../utils/calculations';

/**
 * useDashboardMetrics - Calculate all dashboard KPIs
 * Returns comprehensive metrics for dashboard display
 */
export default function useDashboardMetrics(invoices = []) {
  return useMemo(() => {
    if (!invoices || invoices.length === 0) {
      return {
        totalOutstanding: 0,
        revenueThisMonth: 0,
        overdueCount: 0,
        overdueAmount: 0,
        draftCount: 0,
        draftValue: 0,
        collectionRate: 0,
        revenueGrowth: 0,
        paymentSuccessRate: 0,
        averagePaymentTime: 0,
        sparklines: {
          outstanding: [],
          revenue: [],
          overdue: [],
          drafts: [],
        },
      };
    }

    const now = new Date();
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    
    // Calculate basic metrics
    let totalOutstanding = 0;
    let revenueThisMonth = 0;
    let overdueCount = 0;
    let overdueAmount = 0;
    let draftCount = 0;
    let draftValue = 0;
    
    invoices.forEach(invoice => {
      // Outstanding
      if ((invoice.status === 'sent' || invoice.status === 'partially_paid') && invoice.balance_due > 0) {
        totalOutstanding += invoice.balance_due;
      }
      
      // Revenue this month
      if (invoice.status === 'paid') {
        const paidDate = new Date(invoice.updated_at);
        if (paidDate >= thisMonthStart) {
          revenueThisMonth += invoice.total;
        }
      }
      
      // Overdue
      if ((invoice.status === 'sent' || invoice.status === 'partially_paid') && 
          new Date(invoice.due_date) < now && 
          invoice.balance_due > 0) {
        overdueCount++;
        overdueAmount += invoice.balance_due;
      }
      
      // Drafts
      if (invoice.status === 'draft') {
        draftCount++;
        draftValue += invoice.total;
      }
    });
    
    // Calculate advanced metrics
    const collectionRate = calculateCollectionRate(invoices);
    const revenueGrowth = calculateRevenueGrowth(invoices);
    const paymentSuccessRate = calculatePaymentSuccessRate(invoices);
    const averagePaymentTime = calculateAveragePaymentTime(invoices);
    
    // Generate sparkline data
    const sparklines = {
      outstanding: generateSparklineData(invoices, 7, 'revenue'),
      revenue: generateSparklineData(invoices, 7, 'revenue'),
      overdue: generateSparklineData(invoices, 7, 'count'),
      drafts: generateSparklineData(invoices, 7, 'count'),
    };
    
    return {
      totalOutstanding,
      revenueThisMonth,
      overdueCount,
      overdueAmount,
      draftCount,
      draftValue,
      collectionRate,
      revenueGrowth,
      paymentSuccessRate,
      averagePaymentTime,
      sparklines,
    };
  }, [invoices]);
}
